import styled from "@emotion/styled";
import {
  Dialog,
  DialogActions,
  DialogContent,
  Typography,
  Fade,
  DialogTitle,
} from "@mui/material";
import { SubmitButton } from "@avenue-8/sidekick-ui";
import { useCallback, useState } from "react";

const CustomDialogTitle = styled(DialogTitle)`
  font-size: ${(p) => p.theme.title.fontSizes.h4};
  padding: 1.5rem 1.5rem 1.5rem 2rem;
`;

const CustomDialog = styled(Dialog)`
  transition: all 0.5s ease;
  & .MuiDialog-paper {
    padding: 1rem;
    min-height: 46vh;
    background-color: ${(p) => p.theme.colors.background};
    transition: all 0.5s ease;
    max-width: 640px;

    ${(p) => p.theme.media.mobileDown} {
      padding: 0rem;
      min-height: min(540px, 80vh);
    }
  }
`;

const CustomContent = styled(DialogContent)`
  padding: 2rem;
  overflow: hidden;
`;

type Props = {
  userFirstName: string;
  open: boolean;
  isLoading: boolean;
  onConfirmCompletedWelcome: () => void;
};

export const WelcomeDialog = (props: Props) => {
  const { userFirstName, open, onConfirmCompletedWelcome, isLoading } = props;
  const [step, setStep] = useState(0);

  const handleNext = useCallback(async () => {
    if (step === 0) {
      setStep(1);
      return;
    }
    if (step === 1) {
      onConfirmCompletedWelcome();
    }
  }, [step, onConfirmCompletedWelcome]);

  return (
    <CustomDialog fullWidth open={open} maxWidth="md">
      {step === 0 && <CustomDialogTitle>Hi, {userFirstName}</CustomDialogTitle>}
      {step === 0 && (
        <Fade in={true} timeout={1000}>
          <CustomContent>
            <Typography fontSize={20} mb={2}>
              Welcome to Sidekick - an AI assistant built for real estate
              professionals like you.
            </Typography>
            <Typography fontSize={20} mb={1}>
              I can help with anything from analyzing the market, client
              communications, business planning, and marketing help.
            </Typography>
          </CustomContent>
        </Fade>
      )}

      {step === 1 && (
        <Fade in={true} timeout={1000}>
          <CustomContent>
            <Typography fontSize={20} mb={2}>
              Just like a human assistant, I may not always get it right the
              first time.
            </Typography>
            <Typography fontSize={20} mb={8}>
              To get the best results out of me, try asking questions a few
              different ways so we can learn how to work together.
            </Typography>
            <Typography fontSize={20} fontWeight={700}>
              Let me show you how!
            </Typography>
          </CustomContent>
        </Fade>
      )}

      <DialogActions>
        <SubmitButton
          onClick={handleNext}
          sx={{ minWidth: "160px" }}
          type="button"
          isLoading={isLoading}
        >
          {step === 0 ? "Next" : "Get Started"}
        </SubmitButton>
      </DialogActions>
    </CustomDialog>
  );
};
